/**
 * Permission utility functions for managing user permissions and access control
 */

export interface Permission {
  codename: string;
  name: string;
}

export interface Group {
  id: number;
  name: string;
  description: string;
  is_role: boolean;
  permissions: Permission[];
}

export interface UserWithGroups {
  groups: Group[];
  [key: string]: any;
}

/**
 * Merges all permissions from all role groups (is_role: true)
 * @param groups - Array of user groups
 * @returns Array of unique permission codenames
 */
export function mergePermissionsFromGroups(groups: Group[]): string[] {
  if (!groups || groups.length === 0) {
    return [];
  }

  // Filter only role groups (is_role: true)
  const roleGroups = groups.filter((group) => group.is_role === true);

  // Extract all permissions from role groups
  const allPermissions: string[] = [];
  roleGroups.forEach((group) => {
    if (group.permissions && Array.isArray(group.permissions)) {
      group.permissions.forEach((permission) => {
        allPermissions.push(permission.codename);
      });
    }
  });

  // Return unique permissions
  return Array.from(new Set(allPermissions));
}

/**
 * Checks if user has a specific permission
 * @param userPermissions - Array of user's permission codenames
 * @param requiredPermission - Required permission codename
 * @returns true if user has the permission
 */
export function hasPermission(userPermissions: string[], requiredPermission: string): boolean {
  return userPermissions.includes(requiredPermission);
}

/**
 * Checks if user has any of the required permissions
 * @param userPermissions - Array of user's permission codenames
 * @param requiredPermissions - Array of required permission codenames
 * @returns true if user has at least one of the required permissions
 */
export function hasAnyPermission(userPermissions: string[], requiredPermissions: string[]): boolean {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true; // No permissions required
  }
  return requiredPermissions.some((permission) => userPermissions.includes(permission));
}

/**
 * Checks if user has all of the required permissions
 * @param userPermissions - Array of user's permission codenames
 * @param requiredPermissions - Array of required permission codenames
 * @returns true if user has all required permissions
 */
export function hasAllPermissions(userPermissions: string[], requiredPermissions: string[]): boolean {
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return true; // No permissions required
  }
  return requiredPermissions.every((permission) => userPermissions.includes(permission));
}

const normalizeRoleName = (value: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase();

const collectRoleNames = (user: any): string[] => {
  const roles = new Set<string>();
  const directRole = user?.role;

  if (Array.isArray(directRole)) {
    directRole.forEach((item: any) => {
      if (item === 0 || item === "0") {
        roles.add("admin");
        return;
      }
      if (typeof item === "string") {
        const normalized = normalizeRoleName(item);
        if (normalized) roles.add(normalized);
        return;
      }
      if (item && typeof item === "object" && item.name) {
        const normalized = normalizeRoleName(item.name);
        if (normalized) roles.add(normalized);
      }
    });
  } else if (directRole === 0 || directRole === "0") {
    roles.add("admin");
  } else {
    const normalized = normalizeRoleName(directRole);
    if (normalized) roles.add(normalized);
  }

  if (Array.isArray(user?.groups)) {
    user.groups.forEach((group: any) => {
      const normalized = normalizeRoleName(group?.name);
      if ((group?.is_role === true || group?.is_role === "true") && normalized) {
        roles.add(normalized);
      }
    });
  }

  return Array.from(roles);
};

/**
 * Checks if a user has any of the provided role names.
 * Role matching is case-insensitive and includes role groups where is_role is true.
 */
export function hasAnyRole(user: any, requiredRoles: string[]): boolean {
  if (!user || !requiredRoles || requiredRoles.length === 0) {
    return false;
  }
  const normalizedRequired = requiredRoles.map(normalizeRoleName).filter(Boolean);
  if (normalizedRequired.length === 0) {
    return false;
  }
  const userRoles = collectRoleNames(user);
  return userRoles.some((role) => normalizedRequired.includes(role));
}

/**
 * Standard utility to check if a user is a Tenant Administrator.
 * Handles different role formats from JWT and API responses.
 * @param user - User object with role and groups
 */
export function isTenantAdmin(user: any): boolean {
  if (!user) return false;

  // 1. Direct role field (JWT/Login)
  const r = user.role;
  if (r === 0 || r === "0") return true;
  if (typeof r === "string" && r.toLowerCase() === "admin") return true;

  // 2. Role array/objects
  if (Array.isArray(r)) {
    return r.some((item: any) => {
      if (item === 0 || item === "0") return true;
      if (typeof item === "string") return item.toLowerCase() === "admin";
      if (item && typeof item === "object" && item.name) {
        return String(item.name).toLowerCase() === "admin";
      }
      return false;
    });
  }

  // 3. Groups membership (some backend versions return groups with is_role flag)
  if (Array.isArray(user.groups)) {
    if (
      user.groups.some((g: any) => {
        const name = String(g?.name || "").toLowerCase();
        return (g?.is_role === true || g?.is_role === "true") && name === "admin";
      })
    ) {
      return true;
    }
  }

  // 4. Superuser flag
  return !!user.is_superuser;
}
